import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { describeTalkTime, type UserPlan } from "@/lib/api/models/plan";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Label } from "@/ui/Text";
import { usePlan } from "@/features/plan/use-plan";
import { LANGUAGE_LABEL, LANGUAGE_QUERY_KEY, MEMORY_QUERY_KEY, SITE, SOCIAL_QUERY_KEY } from "./keys";


function planValue(plan: UserPlan | undefined): string {
  if (!plan) return "…";
  if (!plan.active) return `${describeTalkTime(plan)} · see the plan`;
  if (!plan.renews) return "Cancelled. Everything stays open until it ends.";
  return describeTalkTime(plan);
}

function memoryValue(count: number | undefined): string {
  if (count === undefined) return "…";
  return `${count} ${count === 1 ? "thing" : "things"} remembered. Read or remove any of them.`;
}

/** The account, as a ledger of rows. Sign out and delete are rows too, in the quietest voice. */
export function YouTab() {
  const { api, session, analytics } = useServices();
  const navigate = useNavigate();
  const plan = usePlan();
  const social = useQuery({ queryKey: SOCIAL_QUERY_KEY, queryFn: () => api.fetchSocialProfile() });
  const memory = useQuery({ queryKey: MEMORY_QUERY_KEY, queryFn: () => api.fetchMemory() });
  const language = useQuery({ queryKey: LANGUAGE_QUERY_KEY, queryFn: () => api.getLanguagePreference() });

  const handle = social.data?.username;
  const publicPage = handle
    ? `${SITE}/@${handle} · ${social.data?.profile_public ? "on" : "off"}`
    : "No handle yet. Claim one and your card has an address.";

  return (
    <Page>
      <Label>You</Label>
      <Row label="Plan" value={planValue(plan.data)} onClick={() => navigate("/you/plan")} />
      <Row label="Memory" serif={false} value={memoryValue(memory.data?.length)} onClick={() => navigate("/you/memory")} />
      <Row label="Public page" serif={false} value={publicPage} onClick={() => navigate("/you/page")} />
      <Row label="Language" serif={false} value={LANGUAGE_LABEL[language.data ?? "en"] ?? "English"} onClick={() => navigate("/you/language")} />
      <Row label="Privacy" serif={false} value="What leaves this browser, and what never does." onClick={() => navigate("/you/privacy")} />
      <Row
        value={<span className="label text-ink">Sign out</span>}
        onClick={() => {
          analytics.track({ name: "signed out" });
          void session.signOut().then(() => navigate("/", { replace: true }));
        }}
      />
      <Row value={<span className="label text-ink">Delete my account</span>} onClick={() => navigate("/you/delete")} />
    </Page>
  );
}
